/**
 * PATH: packages/pricelist/src/DiscountDialog.tsx
 * FILE_ID: PKG-PRICELIST-DISCOUNT-001
 * PURPOSE: Fixed supplier discount dialog (2026-07-15 task 3) — enter a
 *          discount %, tick the section's products it applies to (default
 *          all), Apply writes data.discount + data.discounted_cost (net
 *          supplier cost = list price x (1 - d/100)) + last-update stamps
 *          via the apply_discount endpoint.
 * WHY: Some suppliers price as list + fixed account discount, not qty tiers.
 * RELATED: PricelistWorkbook.tsx (host), createPricelistClient.applyDiscount
 */
import { useMemo, useState } from 'react'
import { Loader2, Percent, Search, X } from 'lucide-react'
import type { GridRow } from './columns'

export interface DiscountDialogProps {
  open: boolean
  /** The active (supplier) section's name + rows. */
  sectionName: string
  rows: GridRow[]
  saving?: boolean
  onClose: () => void
  onApply: (discount: number, itemIds: string[] | undefined) => void
}

export default function DiscountDialog({
  open,
  sectionName,
  rows,
  saving = false,
  onClose,
  onApply,
}: DiscountDialogProps) {
  const [discount, setDiscount] = useState('')
  // Unticked ids (default = everything selected, the common case).
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  // Product filter so a per-product override doesn't mean untick-the-rest:
  // search, "None", tick the few products, apply.
  const [filter, setFilter] = useState('')
  const persisted = useMemo(() => rows.filter((r) => !r.__new), [rows])
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return persisted
    return persisted.filter((r) =>
      [r.item_name, r.description, r.sku, r.product_code]
        .map((v) => String(v ?? '').toLowerCase())
        .some((s) => s.includes(q)),
    )
  }, [persisted, filter])
  const selectedCount = persisted.length - excluded.size

  if (!open) return null
  const value = Number(discount)
  const valid = discount.trim() !== '' && Number.isFinite(value) && value >= 0 && value <= 100

  const toggle = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col gap-3 rounded-xl bg-white p-4 shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Supplier discount — {sectionName}
          </h3>
          <button type="button" aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Net supplier cost = list price × (1 − discount%). Applies to the ticked products;
          untick any priced differently, then re-run with their own rate. Stamps a
          last-price-update date on the supplier and each updated product.
        </p>
        <label className="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-100">
          <Percent className="h-4 w-4 text-slate-400" />
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            placeholder="e.g. 38"
            className="h-8 w-28 rounded border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            aria-label="Discount percentage"
          />
          <span className="text-xs text-slate-500">% off list price</span>
        </label>
        <div className="flex items-center gap-2">
          <label className="relative flex-1">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter products…"
              aria-label="Filter products"
              className="h-8 w-full rounded border border-slate-300 bg-white pl-7 pr-2 text-xs text-slate-900 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </label>
          <button
            type="button"
            className="qb-pl-btn text-xs"
            onClick={() => setExcluded(new Set())}
            title="Tick every product (supplier-wide discount)"
          >
            All
          </button>
          <button
            type="button"
            className="qb-pl-btn text-xs"
            onClick={() => setExcluded(new Set(persisted.map((r) => r.id)))}
            title="Untick every product, then tick the subset for this rate"
          >
            None
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
          {shown.map((row) => (
            <label
              key={row.id}
              className="flex items-center gap-2 border-b border-slate-100 px-2 py-1.5 text-xs text-slate-800 last:border-b-0 dark:border-slate-800 dark:text-slate-100"
            >
              <input
                type="checkbox"
                checked={!excluded.has(row.id)}
                onChange={() => toggle(row.id)}
                aria-label={`Include ${String(row.item_name ?? row.sku ?? row.id)}`}
              />
              <span className="flex-1 truncate">{String(row.item_name || row.description || row.sku || row.id)}</span>
              <code className="text-[10px] text-slate-400">{String(row.sku ?? '')}</code>
            </label>
          ))}
          {persisted.length === 0 && (
            <p className="p-3 text-center text-xs text-slate-400">No saved products in this section.</p>
          )}
          {persisted.length > 0 && shown.length === 0 && (
            <p className="p-3 text-center text-xs text-slate-400">No products match the filter.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-[11px] text-slate-500">{selectedCount} of {persisted.length} selected</span>
          <button type="button" onClick={onClose} className="qb-pl-btn">Cancel</button>
          <button
            type="button"
            disabled={!valid || saving || selectedCount === 0}
            onClick={() =>
              onApply(
                value,
                excluded.size === 0
                  ? undefined // section-wide: also records the supplier default
                  : persisted.filter((r) => !excluded.has(r.id)).map((r) => r.id),
              )
            }
            className="qb-pl-btn-primary disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Percent className="h-4 w-4" />}
            Apply discount
          </button>
        </div>
      </div>
    </div>
  )
}
